// frontend/src/components/HospitalSidebar.js
import React, { useRef, useEffect, useCallback } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import '../styles/HospitalSidebar.css';

const DraggableItem = ({ id, data, children, dataTour }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data: { value: data },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="department-item"
      data-tour={dataTour}
    >
      {children}
    </div>
  );
};

const HospitalSidebar = ({ hospitals = {}, isOpen, toggleSidebar, dataTour }) => {
  const sidebarRef = useRef(null);
  const toggleButtonRef = useRef(null);

  const updateToggleButtonPosition = useCallback(() => {
    if (toggleButtonRef.current && sidebarRef.current) {
      const sidebarWidth = sidebarRef.current.offsetWidth;
      toggleButtonRef.current.style.right = isOpen ? `${sidebarWidth}px` : '0';
    }
  }, [isOpen]);

  useEffect(() => {
    updateToggleButtonPosition();
    window.addEventListener('resize', updateToggleButtonPosition);
    return () => {
      window.removeEventListener('resize', updateToggleButtonPosition);
    };
  }, [updateToggleButtonPosition]);

  /**
   * 部署一覧から重複を除外＆ソート
   */
  const getUniqueSortedDepartments = (departments) => {
    const uniqueDepartments = departments.reduce((acc, dept) => {
      const key = `${dept.department_id}-${dept.display_name}`;
      if (!acc[key] || dept.term_id < acc[key].term_id) {
        acc[key] = dept;
      }
      return acc;
    }, {});
    return Object.values(uniqueDepartments).sort((a, b) =>
      a.display_name.localeCompare(b.display_name)
    );
  };

  return (
    <>
      {/* トグルボタン */}
      <button
        ref={toggleButtonRef}
        className={`sidebar-toggle fixed top-1/2 transform -translate-y-1/2 bg-primary text-white p-2 rounded-l-full shadow-md hover:bg-primary-dark transition-colors duration-200 ${
          isOpen ? 'open' : 'closed'
        }`}
        onClick={toggleSidebar}
      >
        {isOpen ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
      </button>

      {/* サイドバー本体 */}
      <div ref={sidebarRef} className={`hospital-sidebar ${isOpen ? 'open' : 'closed'}`} data-tour={dataTour || "first-hospital-sidebar"}>
        <h2 className="text-xl font-bold mb-2 text-gray-800">病院一覧</h2>
        <div className="sidebar-content">
          {Object.entries(hospitals).map(([institutionId, institution], institutionIndex) => {
            const uniqueDepartments = getUniqueSortedDepartments(institution.departments);
            return (
              /*
                以前と同様に、hospital-box に
                "bg-white rounded-lg shadow-md mb-3" を復元。
                CSSで書くなら .hospital-box に記述しても良いですが、
                Tailwindクラスで一括指定が手軽です。
              */
              <div
                key={institutionId}
                className="hospital-box bg-white rounded-lg shadow-md mb-3"
                data-tour={institutionIndex === 0 ? 'first-hospital-box' : undefined}
              >
                <h3 className="text-lg font-semibold mb-2 hospital-name">{institution.name}</h3>
                <div className="department-list">
                  {uniqueDepartments.map((department, deptIndex) => (
                    <DraggableItem
                      key={`${institutionId}-${department.id}`}
                      id={`hospital-${institutionId}-${department.id}`}
                      data={department}
                      dataTour={institutionIndex === 0 && deptIndex === 0 ? 'first-department-item' : undefined}
                    >
                      {department.display_name}
                    </DraggableItem>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default HospitalSidebar;
